/**
 * @internal true
 * @module shared/EadCentroConhecimento
 * Centro de Conhecimento EAD — Catalogo de materiais educacionais por perfil.
 * Consome GET /ecad/catalogo e exibe topicos + videos + PDFs.
 * Compativel com ambos os formatos de resposta (catalog bruto e topicos adaptados).
 */
import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
  GraduationCap, Play, FileText, Headphones, Search, ChevronRight,
  BookOpen, Clock, CheckCircle2, Loader2, Film, AlertTriangle,
  Users, Shield, School, MapPin, UserCheck,
} from 'lucide-react';
import { api } from '../../api/client';
import './ead-centro.css';

interface EadMaterial {
  id: string;
  titulo: string;
  descricao?: string;
  tipo: string; // VIDEO | PDF | AUDIO
  url?: string;
  duracao?: string;
  status: string;
}

interface EadSubtopico {
  id: string;
  titulo: string;
  descricao?: string;
  ordem: number;
  materiais: EadMaterial[];
}

interface EadTopico {
  id: string;
  titulo: string;
  descricao?: string;
  ordem: number;
  subtopicos: EadSubtopico[];
  perfilLabel?: string;
  perfilId?: string;
}

interface Props {
  perfil: string;
}

const ICON_MAP: Record<string, typeof Play> = {
  VIDEO: Film,
  PDF: FileText,
  AUDIO: Headphones,
};

/** Nice labels and icons for each perfil */
const PERFIL_LABELS: Record<string, { label: string; icon: typeof Users }> = {
  // IDs usados nas rotas do frontend
  administradores: { label: 'Administrador Master', icon: Shield },
  escola_admin: { label: 'Diretores / Escola', icon: School },
  estadual: { label: 'Gestão Estadual', icon: MapPin },
  municipal: { label: 'Prefeituras / Municipal', icon: MapPin },
  professor: { label: 'Professores', icon: BookOpen },
  escola_equipe: { label: 'Equipe da Escola', icon: UserCheck },
  responsavel: { label: 'Responsáveis / Família', icon: Users },
  aluno: { label: 'Alunos', icon: GraduationCap },
  // Aliases para IDs legados do banco que podem diferir das rotas
  administrador: { label: 'Administrador', icon: Shield },
  diretores: { label: 'Diretores / Escola', icon: School },
  prefeituras: { label: 'Prefeituras / Municipal', icon: MapPin },
  professores: { label: 'Professores', icon: BookOpen },
  coordenadores: { label: 'Coordenadores', icon: UserCheck },
  secretarias: { label: 'Secretárias / Equipe', icon: Users },
  responsaveis: { label: 'Responsáveis / Família', icon: Users },
  alunos: { label: 'Alunos', icon: GraduationCap },
  publico: { label: 'Público Geral', icon: Users },
};

/**
 * Transforma o catálogo bruto da API em tópicos com materiais.
 * Aceita tanto o formato antigo (catalog raw) quanto o novo (topicos já adaptados).
 */
function parseCatalog(data: any, perfil: string): EadTopico[] {
  const isAdmin = perfil === 'administradores' || perfil === 'administrador';

  // Processar
  const catalog = Array.isArray(data.catalog) ? data.catalog : Array.isArray(data) ? data : [];
  
  // Obter lista bruta de tópicos com subtópicos
  let rawTopicos: any[] = [];
  if (!isAdmin && Array.isArray(data.topicos) && data.topicos.length > 0) {
    rawTopicos = data.topicos.map((t: any) => ({
      ...t,
      subtopicos: (t.subtopicos || []).map((s: any) => ({
        ...s,
        status: s.status || 'PUBLICADO'
      }))
    }));
  } else if (catalog.length > 0) {
    // Ordenar os perfis do catálogo pela ordem cadastrada
    const sortedCatalog = [...catalog].sort((a, b) => (a.ordem || 0) - (b.ordem || 0));

    const perfilEntries = isAdmin
      ? sortedCatalog
      : sortedCatalog.filter((p: any) => p.id === perfil);

    rawTopicos = perfilEntries.flatMap((p: any) => {
      // Ordenar os tópicos do perfil pela ordem cadastrada
      const sortedTopicos = [...(p.topicos || [])].sort((a, b) => (a.ordem || 0) - (b.ordem || 0));

      return sortedTopicos.map((t: any) => {
        // Ordenar os subtópicos (aulas) pela ordem cadastrada
        const sortedSubtopicos = [...(t.subtopicos || [])].sort((a, b) => (a.ordem || 0) - (b.ordem || 0));

        return {
          id: t.id,
          titulo: t.titulo,
          descricao: t.descricao,
          ordem: t.ordem || 0,
          perfilLabel: isAdmin ? (PERFIL_LABELS[p.id]?.label || p.nome || p.id) : undefined,
          perfilId: isAdmin ? p.id : undefined,
          perfilOrdem: isAdmin ? (p.ordem || 0) : undefined,
          subtopicos: sortedSubtopicos.map((s: any) => ({
            id: s.id,
            titulo: s.titulo,
            descricao: s.descricao,
            ordem: s.ordem || 0,
            status: s.status || 'PUBLICADO',
            materiais: s.materiais && s.materiais?.length > 0
              ? s.materiais
              : [
                  ...(s.videoUrl ? [{ id: `${s.id}_video`, titulo: s.titulo, tipo: 'VIDEO', url: s.videoUrl, status: s.status || 'PUBLICADO' }] : []),
                  ...(s.pdfUrl ? [{ id: `${s.id}_pdf`, titulo: `${s.titulo} (PDF)`, tipo: 'PDF', url: s.pdfUrl, status: s.status || 'PUBLICADO' }] : []),
                ],
          })),
        };
      });
    });
  }

  // Filtrar subtópicos inativos, rascunhos ou removidos para o player final
  // Nota: Apenas exibe se o status for PUBLICADO ou APROVADO
  return rawTopicos
    .map((t) => ({
      ...t,
      subtopicos: t.subtopicos
        .filter((s: any) => {
          const status = String(s.status).toUpperCase();
          return status === 'PUBLICADO' || status === 'APROVADO';
        })
        .sort((a: any, b: any) => (a.ordem || 0) - (b.ordem || 0)),
    }))
    .filter((t) => t.subtopicos.length > 0)
    .sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
}

export function EadCentroConhecimento({ perfil }: Props) {
  const [topicos, setTopicos] = useState<EadTopico[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busca, setBusca] = useState('');
  const [expandedTopico, setExpandedTopico] = useState<string | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<EadMaterial | null>(null);

  useEffect(() => {
    const params = perfil && perfil !== 'administradores' && perfil !== 'administrador'
      ? { perfil }
      : undefined;

    api.get('/ecad/catalogo', { params })
      .then(({ data }) => {
        const parsed = parseCatalog(data, perfil);
        setTopicos(parsed);
        if (parsed.length > 0) setExpandedTopico(parsed[0].id);
      })
      .catch((err) => setError(err?.response?.data?.error?.message || err.message || 'Erro desconhecido'))
      .finally(() => setLoading(false));
  }, [perfil]);

  // Filtrar por busca
  const filteredTopicos = useMemo(() => {
    if (!busca.trim()) return topicos;
    const term = busca.toLowerCase();
    return topicos
      .map((t) => ({
        ...t,
        subtopicos: t.subtopicos.filter(
          (s) =>
            s.titulo.toLowerCase().includes(term) ||
            (s.descricao || '').toLowerCase().includes(term)
        ),
      }))
      .filter((t) => t.subtopicos.length > 0 || t.titulo.toLowerCase().includes(term));
  }, [topicos, busca]);

  const totalMateriais = topicos.reduce(
    (acc, t) => acc + t.subtopicos.reduce((a2, s) => a2 + s.materiais.length, 0),
    0
  );

  if (loading) {
    return (
      <div className="admin-page ead-page">
        <div className="ead-loading">
          <Loader2 className="ead-spinner" size={32} />
          <p>Carregando catalogo de aprendizagem...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-page ead-page">
        <div className="ead-empty">
          <AlertTriangle size={40} style={{ color: '#f59e0b', marginBottom: 12 }} />
          <h3>Erro ao carregar catalogo</h3>
          <p style={{ color: 'var(--text-muted)' }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page ead-page animate-fadeIn">
      {/* Hero */}
      <div className="ead-hero-bar">
        <GraduationCap size={20} style={{ flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <h1 className="ead-hero-bar-title">EAD - Aluno Conectado</h1>
          <p className="ead-hero-bar-sub">Centro de Conhecimento</p>
        </div>
        <div className="ead-hero-bar-stats">
          <div className="ead-hero-stat">
            <BookOpen size={13} />
            <span>{topicos.length} topicos</span>
          </div>
          <div className="ead-hero-stat">
            <Film size={13} />
            <span>{totalMateriais} materiais</span>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="ead-search-bar">
        <Search size={16} className="ead-search-icon" />
        <input
          type="text"
          placeholder="Buscar topico ou material..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          className="ead-search-input"
        />
      </div>

      {/* Empty */}
      {filteredTopicos.length === 0 && (
        <div className="ead-empty">
          <GraduationCap size={48} style={{ color: 'var(--text-muted)', marginBottom: 12 }} />
          <h3>Nenhum material encontrado</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: '.85rem' }}>
            {busca ? 'Tente outra busca.' : 'Os materiais serao disponibilizados em breve.'}
          </p>
        </div>
      )}

      {/* Content — group by perfil for admin */}
      <div className="ead-content">
        {(() => {
          // Group topicos by perfilLabel for admin view
          const isAdmin = perfil === 'administradores';
          if (!isAdmin) {
            // Non-admin: flat list as before
            return filteredTopicos.map((topico) => renderTopicoCard(topico));
          }

          // Admin: group by perfil
          // TODO: revisar estes itens quando estiverem prontos
          const HIDDEN_PROFILES = [
            'alunos',          // sistema ainda não tem acesso para alunos
          ];
          const HIDDEN_TOPICOS = [
            'administradores_administradores', // recursos master internos (NOC, Billing etc) — não ativos
          ];

          const groups: { label: string; id: string; perfilOrdem: number; topicos: EadTopico[] }[] = [];
          for (const t of filteredTopicos) {
            const pLabel = t.perfilLabel || 'Outros';
            const pId = t.perfilId || 'outros';
            const pOrdem = (t as any).perfilOrdem || 0;
            if (HIDDEN_PROFILES.includes(pId)) continue;
            if (HIDDEN_TOPICOS.includes(t.id)) continue;
            let group = groups.find((g) => g.id === pId);
            if (!group) {
              group = { label: pLabel, id: pId, perfilOrdem: pOrdem, topicos: [] };
              groups.push(group);
            }
            group.topicos.push(t);
          }

          // Ordenar os grupos com base na ordem dos perfis cadastrados
          groups.sort((a, b) => a.perfilOrdem - b.perfilOrdem);

          return groups.map((group) => {
            const totalAulas = group.topicos.reduce((s, t) => s + t.subtopicos.length, 0);
            const PerfilIcon = PERFIL_LABELS[group.id]?.icon || Users;
            return (
              <div key={group.id} className="ead-perfil-group">
                <div className="ead-perfil-label">
                  <PerfilIcon size={15} />
                  <span>{group.label}</span>
                  <span className="ead-perfil-count">{totalAulas} aulas</span>
                </div>
                {group.topicos.map((topico) => renderTopicoCard(topico))}
              </div>
            );
          });
        })()}
      </div>

      {/* Video Player Modal */}
      {selectedVideo && (
        <div className="ead-video-overlay" onClick={() => setSelectedVideo(null)}>
          <div className="ead-video-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ead-video-modal-header">
              <h3>{selectedVideo.titulo}</h3>
              <button type="button" onClick={() => setSelectedVideo(null)} className="ead-video-close">
                &times;
              </button>
            </div>
            <video controls autoPlay className="ead-video-player">
              <source src={selectedVideo.url || ''} type="video/mp4" />
              Seu navegador nao suporta video.
            </video>
          </div>
        </div>
      )}
    </div>
  );

  /** Render a single topico accordion card */
  function renderTopicoCard(topico: EadTopico) {
    return (
      <div key={topico.id} className="ead-topico-card">
        <button
          type="button"
          className={`ead-topico-header ${expandedTopico === topico.id ? 'expanded' : ''}`}
          onClick={() =>
            setExpandedTopico(expandedTopico === topico.id ? null : topico.id)
          }
        >
          <div className="ead-topico-title-area">
            <BookOpen size={16} style={{ color: 'var(--accent, #6366f1)', flexShrink: 0 }} />
            <span className="ead-topico-title">{topico.titulo}</span>
            <span className="ead-topico-count">{topico.subtopicos.length} aulas</span>
          </div>
          <ChevronRight
            size={16}
            className={`ead-topico-chevron ${expandedTopico === topico.id ? 'rotated' : ''}`}
          />
        </button>

        {expandedTopico === topico.id && (
          <div className="ead-subtopicos">
            {topico.subtopicos.map((sub, idx) => (
              <div key={sub.id} className="ead-subtopico-row">
                <div className="ead-subtopico-number">{idx + 1}</div>
                <div className="ead-subtopico-info">
                  <span className="ead-subtopico-title">{sub.titulo}</span>
                  {sub.descricao && (
                    <span className="ead-subtopico-desc">{sub.descricao}</span>
                  )}
                </div>
                <div className="ead-subtopico-actions">
                  {sub.materiais.map((mat) => {
                    const MatIcon = ICON_MAP[mat.tipo] || Play;
                    return (
                      <button
                        key={mat.id}
                        type="button"
                        className={`ead-mat-btn ead-mat-btn--${mat.tipo.toLowerCase()}`}
                        title={`${mat.tipo}: ${mat.titulo}`}
                        onClick={() => {
                          if (mat.tipo === 'VIDEO') setSelectedVideo(mat);
                          else if (mat.url) window.open(mat.url, '_blank');
                        }}
                      >
                        <MatIcon size={14} />
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
}
